"use client";

import { ContentLink } from "@components/cms/link/ContentLink";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { StoryTileModel } from "@lib/contentful/mappers/parts/storyTile/types";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import { useInView } from "@ecco/ui/use-in-view";
import type { FC } from "react";
import {
    bannerCss,
    bannerProps,
    getContentProps,
    getHeadlineProps,
    getRootProps,
    getTextBlockAlignSelf,
} from "./SplitCampaignBannerStyles";

const VIEW_TRACKING_DELAY_MS = 2000;

export const SplitCampaignBanner: FC<StoryTileModel> = ({
    id,
    isPageTitle,
    headline,
    description,
    image,
    mobileImage,
    imagePosition = "right",
    whiteBackground,
    links = [],
    termsAndConditionsLink,
}) => {
    const isImageLeft = imagePosition === "left";
    const textAlign = isImageLeft ? "right" : "left";
    const linksJustify = isImageLeft ? "flex-end" : "flex-start";
    const textBlockAlignSelf = getTextBlockAlignSelf(isImageLeft);

    const { ref } = useInView({
        threshold: 0.5,
        onChange: (inView) => {
            if (inView) {
                setTimeout(() => {
                    AnalyticsClient.viewPromotion({
                        image: image?.url ?? mobileImage?.url,
                        title: headline || description,
                        index: 0,
                        id,
                    });
                }, VIEW_TRACKING_DELAY_MS);
            }
        },
    });

    return (
        <Flex
            ref={ref}
            {...getRootProps(isImageLeft, whiteBackground)}
            data-testid="split-campaign-banner"
            data-image-position={imagePosition}
        >
            <Flex {...getContentProps(Boolean(termsAndConditionsLink))} textAlign={textAlign}>
                <Flex {...bannerProps.mainGroup} alignSelf={textBlockAlignSelf}>
                    <Flex {...bannerProps.textGroup}>
                        {headline && (
                            <Box as={isPageTitle ? "h1" : "h2"} {...getHeadlineProps(isPageTitle)}>
                                {headline}
                            </Box>
                        )}
                        {description && <Text {...bannerProps.description}>{description}</Text>}
                    </Flex>
                    {links.length > 0 && (
                        <Flex {...bannerProps.links} justifyContent={linksJustify}>
                            {links.map((link) => (
                                <Button
                                    key={link.id}
                                    as={ContentLink}
                                    link={link}
                                    variant="link"
                                    {...bannerProps.linkButton}
                                    css={bannerCss.linkButtonHover}
                                >
                                    <Box {...bannerProps.linkLabel}>{link.label}</Box>
                                </Button>
                            ))}
                        </Flex>
                    )}
                </Flex>
                {termsAndConditionsLink && (
                    <Flex
                        {...bannerProps.termsLink}
                        justifyContent={linksJustify}
                        alignSelf={textBlockAlignSelf}
                    >
                        <Button
                            as={ContentLink}
                            link={termsAndConditionsLink}
                            variant="link"
                            {...bannerProps.termsLinkButton}
                            css={bannerCss.linkButtonHover}
                        >
                            <Box {...bannerProps.linkLabel}>{termsAndConditionsLink.label}</Box>
                        </Button>
                    </Flex>
                )}
            </Flex>

            <Box {...bannerProps.imageWrapper}>
                {mobileImage && (
                    <NextImage
                        src={mobileImage.url}
                        alt={mobileImage.alt}
                        fill
                        objectFit="cover"
                        sizes={["100vw"]}
                        css={bannerCss.mobileImage}
                    />
                )}
                {image && (
                    <NextImage
                        src={image.url}
                        alt={image.alt}
                        fill
                        objectFit="cover"
                        sizes={["100vw", "100vw", "67vw"]}
                        css={mobileImage ? bannerCss.desktopImageWithMobile : undefined}
                    />
                )}
            </Box>
        </Flex>
    );
};
